import { useSyncExternalStore } from 'react';

/**
 * Shared 30-second ticker for live-match minute displays.
 *
 * One global interval drives every subscriber; subscribers re-render in lock-
 * step on each tick. We use `useSyncExternalStore` so React tears down the
 * subscription cleanly when the last consumer unmounts (no orphaned interval).
 *
 * 30 s is the sweet spot for the displayed minute: the rendered value is
 * an extrapolation of the server's `elapsed_minutes` anchored on
 * `elapsed_updated_at`, both refreshed by the 1-min live-polling job. With
 * a 30 s tick we re-evaluate the extrapolation twice per displayed minute,
 * so the visible value can never lag by more than ~30 s plus the polling
 * gap. Faster ticks would burn CPU re-rendering every card; slower ticks
 * would make the minute counter feel frozen.
 *
 * **Always call with `enabled` set to whether the card is actually live.**
 * The hook must be invoked unconditionally (Rules of Hooks), so we gate the
 * subscription inside via two no-op functions when `enabled === false` — the
 * card subscribes to a stub store that never invalidates, so it never re-
 * renders due to ticks. With ~100 cards mounted in MatchesTab's windowed
 * list, this is the difference between 100 reconciliations per 30 s tick
 * and zero (only live cards trigger work).
 */

let intervalId: number | null = null;
let currentNow = Date.now();
const subscribers = new Set<() => void>();

const start = () => {
  if (intervalId !== null) return;
  intervalId = window.setInterval(() => {
    currentNow = Date.now();
    for (const cb of subscribers) cb();
  }, 30_000);
};

const stop = () => {
  if (intervalId === null) return;
  window.clearInterval(intervalId);
  intervalId = null;
};

const liveSubscribe = (cb: () => void) => {
  subscribers.add(cb);
  start();
  return () => {
    subscribers.delete(cb);
    if (subscribers.size === 0) stop();
  };
};

const liveSnapshot = () => currentNow;

// Stub store for non-live cards — never invalidates, returns a stable
// reference so React skips re-renders triggered by other state changes
// from re-evaluating the subscription. The empty unsubscribe is necessary
// because useSyncExternalStore requires `subscribe` to return a cleanup.
const noopUnsubscribe = () => {};
const stubSubscribe = () => noopUnsubscribe;
const STUB_NOW = 0;
const stubSnapshot = () => STUB_NOW;

export const useLiveTick = (enabled: boolean = true): number =>
  useSyncExternalStore(
    enabled ? liveSubscribe : stubSubscribe,
    enabled ? liveSnapshot : stubSnapshot,
    enabled ? liveSnapshot : stubSnapshot,
  );
